import { CalendarGridColumnName } from '../../../grid/types/column.types';
import {
  ConfigureOptionsParams,
  DescriptionFilterType,
  DoubleDescriptionFilterModel,
  DoubleDescriptionFilterOperator,
  FilterOptions,
  IFilterComparator,
  GridFilterUnionModel,
  SingleDescriptionFilterModel,
  FilterPassParams
} from '../filter-types';
import { DefaultComparator } from './comparators/default-comparator';
import { FilterResultFacade } from '../filter-result-facade';
import { FilterHandlerBase } from './base/filter-handler-base';

export class DescriptionFilterHandler extends FilterHandlerBase {
  private comparator: IFilterComparator<
    SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  >;

  private static isCombinedDescriptionModel(model: unknown): model is {
    operator: DoubleDescriptionFilterOperator;
    conditions: SingleDescriptionFilterModel[];
  } {
    return (
      !!model &&
      typeof model === 'object' &&
      Array.isArray((model as { conditions?: unknown[] }).conditions)
    );
  }

  private static normalizeDoubleDescriptionModel(
    model:
      | Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel>
      | undefined
      | null
  ):
    | Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel> {
    if (!DescriptionFilterHandler.isCombinedDescriptionModel(model)) {
      return model;
    }

    const [condition1, condition2] = model.conditions;

    if (!condition1) {
      return model;
    }

    if (!condition2 || !condition2.filter) {
      return condition1;
    }

    return {
      condition1,
      condition2,
      operator: model.operator
    };
  }

  private static normalizeOperator(
    operator: string | DoubleDescriptionFilterOperator
  ): DoubleDescriptionFilterOperator {
    return String(operator).toUpperCase() ===
      DoubleDescriptionFilterOperator.AND
      ? DoubleDescriptionFilterOperator.AND
      : DoubleDescriptionFilterOperator.OR;
  }

  static getReadableConditionType(type: DescriptionFilterType): string {
    switch (type) {
      case DescriptionFilterType.EndsWith:
        return 'ends with';
      case DescriptionFilterType.StartsWith:
        return 'starts with';
      case DescriptionFilterType.NotContains:
        return 'not contains';
      case DescriptionFilterType.NotEqual:
        return 'not equal';
      default:
        return type;
    }
  }

  static getReadableOperator(
    operator: DoubleDescriptionFilterOperator
  ): string {
    if (!operator) return '';
    return operator === DoubleDescriptionFilterOperator.AND ? 'and' : 'or';
  }

  public static isDoubleDescriptionModel(model: any): boolean {
    if (!model) return false;
    const normalizedModel =
      DescriptionFilterHandler.normalizeDoubleDescriptionModel(model);

    if (!normalizedModel || typeof normalizedModel !== 'object') {
      return false;
    }

    const sdModelType: SingleDescriptionFilterModel = {
      filter: '',
      filterType: undefined,
      type: undefined
    };
    const modelType: DoubleDescriptionFilterModel = {
      condition1: sdModelType,
      condition2: sdModelType,
      operator: DoubleDescriptionFilterOperator.AND
    };
    for (const key in modelType) {
      if (
        Object.prototype.hasOwnProperty.call(modelType, key) &&
        !Object.prototype.hasOwnProperty.call(normalizedModel, key)
      ) {
        return false;
      }
    }
    return true;
  }

  public static getDoubleDescriptionLabel(
    dModel: DoubleDescriptionFilterModel
  ): string {
    const normalizedModel =
      DescriptionFilterHandler.normalizeDoubleDescriptionModel(
        dModel
      ) as DoubleDescriptionFilterModel;
    return `${DescriptionFilterHandler.getReadableConditionType(
      normalizedModel.condition1.type
    )} "${
      normalizedModel.condition1.filter
    }" ${DescriptionFilterHandler.getReadableOperator(
      DescriptionFilterHandler.normalizeOperator(normalizedModel.operator)
    )} ${DescriptionFilterHandler.getReadableConditionType(
      normalizedModel.condition2.type
    )} "${normalizedModel.condition2.filter}"`;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  get name(): string {
    return CalendarGridColumnName.Description;
  }

  configureFilterOptions(params: ConfigureOptionsParams): void {
    return;
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return null;
  }

  toStoreFilterModel(
    model: GridFilterUnionModel,
    filterOptions: FilterOptions
  ): GridFilterUnionModel {
    return super.toStoreFilterModel(
      this.normalizeGridDescriptionModel(model),
      filterOptions
    );
  }

  hasValidDiff(
    options: FilterOptions,
    grid: Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel>,
    store: SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  ): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(
      this.normalizeDescriptionModel(grid),
      this.normalizeStoredDescriptionModel(store)
    );
  }

  getValidDiffComparator(
    options: FilterOptions
  ): IFilterComparator<
    SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  > {
    if (!this.comparator) {
      this.comparator = new DefaultComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  protected getDataFromParams(params: FilterPassParams): {
    id: string;
    description: string;
  } {
    return params.deal;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade) {
    let descriptionFilterPassed = true;
    const description = params.filters[this.name];
    const data = this.getDataFromParams(params);
    if (description) {
      const normalizedModel = this.normalizeDescriptionModel(description.model);
      const isDoubleDescription = Object.prototype.hasOwnProperty.call(
        normalizedModel,
        'operator'
      );
      if (isDoubleDescription) {
        const doubleModel: DoubleDescriptionFilterModel =
          normalizedModel as DoubleDescriptionFilterModel;
        const single1FilterPassed = this.filterSingleDescriptionModel(
          doubleModel.condition1,
          data.description
        );
        const single2FilterPassed = this.filterSingleDescriptionModel(
          doubleModel.condition2,
          data.description
        );
        const operator = DescriptionFilterHandler.normalizeOperator(
          doubleModel.operator
        );

        if (operator === DoubleDescriptionFilterOperator.AND) {
          descriptionFilterPassed = single1FilterPassed && single2FilterPassed;
        } else if (operator === DoubleDescriptionFilterOperator.OR) {
          descriptionFilterPassed = single1FilterPassed || single2FilterPassed;
        }
      } else {
        const singleModel: SingleDescriptionFilterModel =
          normalizedModel as SingleDescriptionFilterModel;
        descriptionFilterPassed = this.filterSingleDescriptionModel(
          singleModel,
          data.description
        );
      }
    }
    result.applyDealFilerResult(data.id, this.name, descriptionFilterPassed);

    return descriptionFilterPassed;
  }

  filterSingleDescriptionModel(
    model: SingleDescriptionFilterModel,
    dealDescription: string
  ): boolean {
    if (!model || !model.type) {
      return true;
    }

    dealDescription = dealDescription ? dealDescription.toUpperCase() : '';
    const filter = model.filter ? model.filter.toUpperCase() : '';
    switch (model.type) {
      case DescriptionFilterType.Equals:
        return dealDescription === filter;
      case DescriptionFilterType.NotEqual:
        return dealDescription !== filter;
      case DescriptionFilterType.Contains:
        return dealDescription.indexOf(filter) !== -1;
      case DescriptionFilterType.NotContains:
        return dealDescription.indexOf(filter) === -1;
      case DescriptionFilterType.StartsWith:
        return dealDescription.startsWith(filter);
      case DescriptionFilterType.EndsWith:
        return dealDescription.endsWith(filter);
      default:
        return true;
    }
  }

  private normalizeDescriptionModel(
    model:
      | Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel>
  ):
    | Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel> {
    return DescriptionFilterHandler.normalizeDoubleDescriptionModel(model);
  }

  private normalizeGridDescriptionModel(
    model: GridFilterUnionModel
  ): GridFilterUnionModel {
    if (!DescriptionFilterHandler.isCombinedDescriptionModel(model)) {
      return model;
    }

    return DescriptionFilterHandler.normalizeDoubleDescriptionModel(
      model
    ) as GridFilterUnionModel;
  }

  private normalizeStoredDescriptionModel(
    model: SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  ): SingleDescriptionFilterModel | DoubleDescriptionFilterModel {
    return DescriptionFilterHandler.normalizeDoubleDescriptionModel(
      model
    ) as SingleDescriptionFilterModel | DoubleDescriptionFilterModel;
  }
}
